/** ag-psd AdjustmentLayer → editor AdjustmentConfig mapping. Pure
 *  module, no DOM / Fabric / React deps so tests can drive it
 *  directly with synthetic ag-psd shapes.
 *
 *  Returns `null` for kinds the editor doesn't yet have a runtime
 *  representation for; the caller (`psd-import.ts`) then falls back
 *  to the flatten-to-raster path (with the original chunk preserved
 *  in the PSD source-stash for export-time restoration).
 *
 *  Matched kinds (10): brightness/contrast, levels, curves, vibrance,
 *  hue/saturation, color balance, invert, threshold, posterize,
 *  black & white.
 *
 *  Deferred kinds (flatten + stash for now): selective color, channel
 *  mixer, photo filter, gradient map, color lookup, exposure. These
 *  have either no editor representation (photo filter / gradient map /
 *  color lookup / exposure) or a representation shape that can't
 *  losslessly hold the full PSD configuration (selective color's
 *  multi-target structure, channel mixer's matrix layout) without a
 *  dedicated mapping spec that we'd want to write alongside the
 *  rendering work — out of scope for this phase. */

import type {
  AdjustmentLayer,
  BrightnessAdjustment,
  LevelsAdjustment,
  CurvesAdjustment,
  VibranceAdjustment,
  HueSaturationAdjustment,
  ColorBalanceAdjustment,
  BlackAndWhiteAdjustment,
  PosterizeAdjustment,
  ThresholdAdjustment,
} from 'ag-psd';
import type { AdjustmentConfig } from '../types';
import type { LevelsChannelConfig, LevelsValue } from './adjustments/levels';
import type { CurvePoint, CurvesValue } from './adjustments/curves';

/** Convert one PSD levels channel block to the editor's per-channel
 *  shape. ag-psd preserves Photoshop's field names; we re-key to the
 *  editor's conventions (input/output black/white + gamma). */
function mapLevelsChannel(psd: NonNullable<LevelsAdjustment['rgb']>): LevelsChannelConfig {
  return {
    inputBlack: psd.shadowInput,
    inputWhite: psd.highlightInput,
    // Photoshop persists midtone as a "gamma * 100" integer; ag-psd
    // documents `midtoneInput` as a 0–253 range where 100 = gamma 1.0.
    // Normalize by /100 to land on the editor's float-gamma scale.
    gamma: psd.midtoneInput / 100,
    outputBlack: psd.shadowOutput,
    outputWhite: psd.highlightOutput,
  };
}

function identityLevelsChannel(): LevelsChannelConfig {
  return {
    inputBlack: 0,
    inputWhite: 255,
    gamma: 1,
    outputBlack: 0,
    outputWhite: 255,
  };
}

function mapLevels(psd: LevelsAdjustment): AdjustmentConfig {
  const value: LevelsValue = {
    rgb: psd.rgb ? mapLevelsChannel(psd.rgb) : identityLevelsChannel(),
    r: psd.red ? mapLevelsChannel(psd.red) : identityLevelsChannel(),
    g: psd.green ? mapLevelsChannel(psd.green) : identityLevelsChannel(),
    b: psd.blue ? mapLevelsChannel(psd.blue) : identityLevelsChannel(),
  };
  return { kind: 'levels', ...value };
}

/** Convert ag-psd's curve points (`{ input, output }`) to the editor's
 *  `CurvePoint` shape (`{ x, y }`). Endpoints are always present in
 *  the editor's identity curve, so an empty PSD channel maps to a
 *  fresh identity curve rather than an empty array (which the LUT
 *  builder treats as a single horizontal line at output=0). */
function mapCurveChannel(psdChannel: CurvesAdjustment['rgb'] | undefined): CurvePoint[] {
  if (!psdChannel || psdChannel.length === 0) {
    return [
      { x: 0, y: 0 },
      { x: 255, y: 255 },
    ];
  }
  return psdChannel.map((pt) => ({ x: pt.input, y: pt.output }));
}

function mapCurves(psd: CurvesAdjustment): AdjustmentConfig {
  const value: CurvesValue = {
    rgb: mapCurveChannel(psd.rgb),
    r: mapCurveChannel(psd.red),
    g: mapCurveChannel(psd.green),
    b: mapCurveChannel(psd.blue),
  };
  return { kind: 'curves', ...value };
}

function mapBrightnessContrast(psd: BrightnessAdjustment): AdjustmentConfig {
  return {
    kind: 'brightness-contrast',
    brightness: psd.brightness ?? 0,
    contrast: psd.contrast ?? 0,
  };
}

function mapVibrance(psd: VibranceAdjustment): AdjustmentConfig {
  return {
    kind: 'vibrance',
    vibrance: psd.vibrance ?? 0,
    saturation: psd.saturation ?? 0,
  };
}

function mapHueSaturation(psd: HueSaturationAdjustment): AdjustmentConfig {
  // Editor v1 ships Master mode only — see comment in
  // `lib/adjustments/hue-saturation.ts`. PSD per-color-band data
  // (reds/yellows/greens/...) is lost in the editor view but preserved
  // in the stash for export-time restoration.
  const master = psd.master;
  return {
    kind: 'hue-saturation',
    hue: master?.hue ?? 0,
    saturation: master?.saturation ?? 0,
    lightness: master?.lightness ?? 0,
  };
}

function mapColorBalance(psd: ColorBalanceAdjustment): AdjustmentConfig {
  const triple = (v: ColorBalanceAdjustment['shadows'] | undefined): [number, number, number] => [
    v?.cyanRed ?? 0,
    v?.magentaGreen ?? 0,
    v?.yellowBlue ?? 0,
  ];
  return {
    kind: 'color-balance',
    shadows: triple(psd.shadows),
    midtones: triple(psd.midtones),
    highlights: triple(psd.highlights),
    preserveLuminosity: psd.preserveLuminosity ?? true,
  };
}

function mapPosterize(psd: PosterizeAdjustment): AdjustmentConfig {
  return { kind: 'posterize', levels: psd.levels ?? 4 };
}

function mapThreshold(psd: ThresholdAdjustment): AdjustmentConfig {
  return { kind: 'threshold', threshold: psd.level ?? 128 };
}

function mapBlackWhite(psd: BlackAndWhiteAdjustment): AdjustmentConfig {
  // PSD reds/yellows/greens/cyans/blues/magentas are signed percentages
  // (typically -200..300). The editor uses `Record<string, number>`
  // weights without a normalized scale; pass through verbatim so the
  // editor or future render code can decide on a scale.
  return {
    kind: 'black-white',
    weights: {
      reds: psd.reds ?? 40,
      yellows: psd.yellows ?? 60,
      greens: psd.greens ?? 40,
      cyans: psd.cyans ?? 60,
      blues: psd.blues ?? 20,
      magentas: psd.magentas ?? 80,
    },
  };
}

/** Map an ag-psd AdjustmentLayer to an editor `AdjustmentConfig`.
 *
 *  Returns `null` when the kind can't be losslessly represented in
 *  the editor's current model — the caller should flatten the layer
 *  to raster and rely on the PSD source-stash for export-time
 *  restoration.
 *
 *  Returns a `{ config, lossy }` tuple — `lossy: true` marks kinds
 *  where the editor's shape is a strict subset of the PSD's (e.g.
 *  hue/saturation's Master-only fallback drops per-color-band data,
 *  black-white discards the tint color). Lossy mappings still
 *  produce an editor adjustment LayerNode, but the caller emits a
 *  warning so the user knows the editor view may not fully match
 *  the original — the stash export path restores fidelity. */
export function mapPsdAdjustment(
  psd: AdjustmentLayer,
): { config: AdjustmentConfig; lossy: boolean } | null {
  switch (psd.type) {
    case 'brightness/contrast':
      return { config: mapBrightnessContrast(psd), lossy: false };
    case 'levels':
      return { config: mapLevels(psd), lossy: false };
    case 'curves':
      return { config: mapCurves(psd), lossy: false };
    case 'vibrance':
      return { config: mapVibrance(psd), lossy: false };
    case 'hue/saturation': {
      // Lossy when any of the per-color-band channels carry non-
      // identity values — the editor only renders Master mode in v1.
      const hasBands = !!(
        psd.reds ||
        psd.yellows ||
        psd.greens ||
        psd.cyans ||
        psd.blues ||
        psd.magentas
      );
      return { config: mapHueSaturation(psd), lossy: hasBands };
    }
    case 'color balance':
      return { config: mapColorBalance(psd), lossy: false };
    case 'invert':
      return { config: { kind: 'invert' }, lossy: false };
    case 'posterize':
      return { config: mapPosterize(psd), lossy: false };
    case 'threshold':
      return { config: mapThreshold(psd), lossy: false };
    case 'black & white':
      // PSD's `useTint` + `tintColor` are not in the editor's
      // representation; mark lossy when tinting was used.
      return { config: mapBlackWhite(psd), lossy: !!psd.useTint };

    // Deferred kinds — no editor representation that can hold the
    // PSD configuration losslessly. Caller flattens to raster; the
    // PSD source-stash preserves the original chunk for export.
    case 'selective color':
    case 'channel mixer':
    case 'photo filter':
    case 'gradient map':
    case 'color lookup':
    case 'exposure':
      return null;

    default:
      // Unknown type — defensive default for future ag-psd additions.
      // Flatten + stash.
      return null;
  }
}

// ---------- Reverse mapper (editor → ag-psd) -------------------------------

/** Invert `mapPsdAdjustment` — convert an editor `AdjustmentConfig`
 *  back to an ag-psd `AdjustmentLayer` for the PSD export path.
 *  Critical for round-trip: a user who imports a PSD, EDITS a
 *  stash-backed adjustment-layer's config (e.g., changes Curves
 *  curve points), then saves as PSD must see the EDITED config
 *  written, not the stash's original.
 *
 *  Returns null for kinds with no PSD equivalent in `AdjustmentLayer`
 *  (none currently — every editor `AdjustmentConfig` kind maps back).
 *  The filter-* kinds aren't AdjustmentLayer at all — they're separate
 *  PSD layer-effect chunks — so they return null and the caller
 *  falls back to raster emission.
 *
 *  Lossy-direction notes:
 *
 *  - hue/saturation: editor only stores Master mode. The reverse
 *    map writes master only; the original PSD's per-color-band data
 *    (if any) is lost. The stash's pre-edit version is overwritten —
 *    by design, because the user EDITED the adjustment and we want
 *    the edit to take effect.
 *  - black-white: editor doesn't model tint color. Tint is omitted
 *    on reverse.
 *  - levels: midtone gamma → `gamma * 100` integer (Photoshop's
 *    encoding). Round to match Photoshop's slider precision. */
export function unmapEditorAdjustment(config: AdjustmentConfig): AdjustmentLayer | null {
  switch (config.kind) {
    case 'brightness-contrast':
      return {
        type: 'brightness/contrast',
        brightness: config.brightness,
        contrast: config.contrast,
      };

    case 'levels': {
      const channel = (c: LevelsChannelConfig): NonNullable<LevelsAdjustment['rgb']> => ({
        shadowInput: c.inputBlack,
        highlightInput: c.inputWhite,
        midtoneInput: Math.round(c.gamma * 100),
        shadowOutput: c.outputBlack,
        highlightOutput: c.outputWhite,
      });
      return {
        type: 'levels',
        rgb: channel(config.rgb),
        red: channel(config.r),
        green: channel(config.g),
        blue: channel(config.b),
      };
    }

    case 'curves': {
      const channel = (points: CurvePoint[]): CurvesAdjustment['rgb'] =>
        points.map((p) => ({ input: p.x, output: p.y }));
      return {
        type: 'curves',
        rgb: channel(config.rgb),
        red: channel(config.r),
        green: channel(config.g),
        blue: channel(config.b),
      };
    }

    case 'vibrance':
      return {
        type: 'vibrance',
        vibrance: config.vibrance,
        saturation: config.saturation,
      };

    case 'hue-saturation':
      // Editor's HueSaturationValue is Master only. The `a/b/c/d`
      // fields ag-psd requires are anchor params for the saturation
      // map UI in Photoshop's panel — irrelevant for the actual
      // adjustment effect when only Master is used. Set them to 0;
      // Photoshop re-derives on open if needed.
      return {
        type: 'hue/saturation',
        master: {
          a: 0,
          b: 0,
          c: 0,
          d: 0,
          hue: config.hue,
          saturation: config.saturation,
          lightness: config.lightness,
        },
      };

    case 'color-balance':
      return {
        type: 'color balance',
        shadows: {
          cyanRed: config.shadows[0],
          magentaGreen: config.shadows[1],
          yellowBlue: config.shadows[2],
        },
        midtones: {
          cyanRed: config.midtones[0],
          magentaGreen: config.midtones[1],
          yellowBlue: config.midtones[2],
        },
        highlights: {
          cyanRed: config.highlights[0],
          magentaGreen: config.highlights[1],
          yellowBlue: config.highlights[2],
        },
        preserveLuminosity: config.preserveLuminosity,
      };

    case 'invert':
      return { type: 'invert' };

    case 'threshold':
      return { type: 'threshold', level: config.threshold };

    case 'posterize':
      return { type: 'posterize', levels: config.levels };

    case 'black-white':
      return {
        type: 'black & white',
        reds: config.weights.reds,
        yellows: config.weights.yellows,
        greens: config.weights.greens,
        cyans: config.weights.cyans,
        blues: config.weights.blues,
        magentas: config.weights.magentas,
      };

    // Placeholder kinds (editor type union but not yet rendered) —
    // selective-color + channel-mixer also have AdjustmentLayer
    // equivalents; we wire them when render support lands so the
    // reverse mapping doesn't fabricate data the editor can't
    // round-trip back into.
    case 'selective-color':
    case 'channel-mixer':
      return null;

    // filter-* kinds aren't AdjustmentLayer in PSD — they're separate
    // pixel-modifying ops. The export path emits these as raster
    // (the slow-path compositor has already baked their effect into
    // the surface pixels) so this mapper returns null.
    case 'filter-gaussian-blur':
    case 'filter-box-blur':
    case 'filter-motion-blur':
    case 'filter-radial-blur':
      return null;

    default:
      // Defensive — unreachable while every kind is enumerated above,
      // but keeps the function total when AdjustmentConfig grows.
      return null;
  }
}
